import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ImagePlus, Trash2, Link2, ImageOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MediaPanel({ token }) {
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const inputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () =>
    axios.get(`${API}/media`, { headers }).then((r) => setFiles(r.data)).catch(() => setFiles([]));

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      await axios.post(`${API}/media/upload`, data, { headers });
      toast.success("Image uploaded");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/media/${id}`, { headers });
      setFiles((prev) => prev.filter((f) => f.id !== id));
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const copyUrl = (path) => {
    navigator.clipboard.writeText(`${API}/media/file/${path}`);
    toast.success("Image URL copied — send it to me to wire it into the site");
  };

  return (
    <div data-testid="media-panel" className="rounded-xl border border-white/[0.08] bg-[#12161F] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-50">Media Library</h3>
          <p className="mt-1 text-xs text-slate-500">
            Upload testimonial photos or brand assets. Stored files get a public URL you can use anywhere on the site.
          </p>
        </div>
        <input ref={inputRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="media-file-input" />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="media-upload-button"
          className="flex items-center gap-2 rounded-md bg-[#F25F22] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          Upload Image
        </button>
      </div>

      {files === null ? (
        <div className="flex items-center justify-center gap-2 p-12 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : files.length === 0 ? (
        <div className="flex flex-col items-center gap-3 p-12 text-slate-500" data-testid="media-empty">
          <ImageOff className="h-8 w-8" />
          <p className="text-sm">No images yet — upload your first one.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {files.map((f) => (
            <div key={f.id} data-testid={`media-item-${f.id}`} className="group overflow-hidden rounded-lg border border-white/[0.08] bg-[#0B0D10]">
              <img
                src={`${API}/media/file/${f.storage_path}`}
                alt={f.original_filename}
                className="h-32 w-full object-cover"
              />
              <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                <span className="truncate text-[11px] text-slate-500">{f.original_filename}</span>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => copyUrl(f.storage_path)}
                    data-testid={`media-copy-${f.id}`}
                    title="Copy image URL"
                    className="rounded p-1.5 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-slate-200"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => remove(f.id)}
                    disabled={deleting === f.id}
                    data-testid={`media-delete-${f.id}`}
                    title="Delete image"
                    className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-40"
                  >
                    {deleting === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
